import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { env } from '../config/environment'

// Types
interface Customer {
  id: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
}

interface Business {
  id: string
  name: string
  logo?: string
}

interface Job {
  id: string
  title: string
  description?: string
  status: string
  scheduledDate?: string
  completedDate?: string
  serviceAddress: string
  technician?: {
    id: string
    firstName: string
    lastName: string
  }
  vehicle?: {
    id: string
    name: string
    make: string
    model: string
  }
}

interface Invoice {
  id: string
  invoiceNumber: string
  amount: number
  status: string
  dueDate?: string
  paidDate?: string
  jobId?: string
  job?: {
    id: string
    title: string
  }
}

interface Document {
  id: string
  filename: string
  originalName: string
  mimeType: string
  size: number
  uploadedAt: string
  jobId?: string
  invoiceId?: string
}

interface SessionValidationResponse {
  success: boolean
  customer: Customer
  business: Business
  permissions: string[]
  portalConfig?: PortalConfig
}

interface PortalConfig {
  defaultLandingPage: string
  navigationLinks: {
    dashboard: { enabled: boolean; label?: string }
    jobs: { enabled: boolean; label?: string }
    estimates: { enabled: boolean; label?: string }
    invoices: { enabled: boolean; label?: string }
    products: { enabled: boolean; label?: string }
    visualizer: { enabled: boolean; label?: string }
    subscriptions: { enabled: boolean; label?: string }
    profile: { enabled: boolean; label?: string }
  }
}

interface MagicLinkVerificationResponse {
  success: boolean
  /** Legacy field name from some clients */
  token?: string
  customerToken?: string
  customer: Customer
  business: Business
  redirectUrl?: string
  redirectTo?: string
  requiresPasswordSetup?: boolean
  portalConfig?: PortalConfig
}

class CustomerApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: env.apiBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('customerToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = String(error.config?.url ?? '')
          // Portal link verification failures must be handled by AccessPage — never wipe session / hard-redirect here
          if (
            url.includes('/customer-portal/verify-link') ||
            url.includes('/customer-portal/verify/')
          ) {
            return Promise.reject(error)
          }
          localStorage.removeItem('customerToken')

          const currentPath = window.location.pathname
          const hasTokenParam = window.location.search.includes('token=')
          const isEstimatePage = currentPath.includes('/estimates/')

          if (!(isEstimatePage && hasTokenParam)) {
            window.location.href = '/'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  async verifyPortalLink(params: {
    token: string
    password?: string
  }): Promise<MagicLinkVerificationResponse> {
    const response: AxiosResponse<MagicLinkVerificationResponse> = await this.api.post(
      '/customer-portal/verify-link',
      {
        token: params.token,
        password: params.password,
      }
    )
    return response.data
  }

  /** @deprecated Use verifyPortalLink — kept for edge cases */
  async verifyMagicLink(token: string): Promise<MagicLinkVerificationResponse> {
    const response: AxiosResponse<MagicLinkVerificationResponse> = await this.api.get(
      `/customer-portal/verify/${token}`
    )
    return response.data
  }

  async validateSession(): Promise<SessionValidationResponse> {
    const response: AxiosResponse<SessionValidationResponse> = await this.api.get(
      '/customer-portal/validate'
    )
    return response.data
  }


  // Jobs
  async getJobs(): Promise<{ jobs: Job[], total: number }> {
    const response: AxiosResponse<{ jobs: Job[], total: number }> = await this.api.get('/customer-portal/jobs')
    return response.data
  }

  async getDashboardData(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/customer-portal/dashboard')
    return response.data
  }

  async setupPassword(password: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post(
      '/customer-portal/setup-password',
      { password }
    )
    return response.data
  }

  async login(email: string, password: string): Promise<{
    success: boolean
    token: string
    customer: any
    business: any
    permissions: string[]
  }> {
    const response: AxiosResponse<{
      success: boolean
      token: string
      customer: any
      business: any
      permissions: string[]
    }> = await this.api.post('/customer-portal/login', { email, password })
    return response.data
  }

  async getJob(jobId: string): Promise<Job> {
    console.log('🔍 CustomerAPI: Getting job with ID:', jobId)
    console.log('🔍 CustomerAPI: Token present:', !!localStorage.getItem('customerToken'))
    console.log('🔍 CustomerAPI: Making request to:', `/customer-portal/jobs/${jobId}`)
    
    try {
      const response: AxiosResponse<Job> = await this.api.get(`/customer-portal/jobs/${jobId}`)
      console.log('✅ CustomerAPI: Job data received:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ CustomerAPI: Error getting job:', error)
      console.error('❌ CustomerAPI: Error response:', error.response?.data)
      console.error('❌ CustomerAPI: Error status:', error.response?.status)
      throw error
    }
  }

  async createPaymentIntent(
    invoiceId: string, 
    paymentMethod: 'card' | 'ach' | 'both' = 'card'
  ): Promise<{
    success: boolean
    processor?: 'stripe' | 'helcim' | 'finix' | 'banccard'
    clientSecret?: string
    paymentIntentId?: string
    paymentLinkUrl?: string
    checkoutToken?: string
    secretToken?: string
    publishableKey?: string
    paymentLinkId?: string
    paymentMethodTypes?: string[]
    finix?: {
      applicationId: string | null
      merchantId: string
      merchantIdentityId: string | null
      environment: 'sandbox' | 'live'
      amount: number
      currency: string
      applePayEnabled?: boolean
      googlePayEnabled?: boolean
      achEnabled?: boolean
      paymentMethods?: Array<'card' | 'bank'>
    }
    amount: number
    currency: string
    reused?: boolean
    invoice: {
      id: string
      invoiceNumber: string
      totalAmount: number
      dueAt: string
    }
  }> {
    const response = await this.api.post(`/customer-portal/invoices/${invoiceId}/payment-intent`, {
      paymentMethod
    })
    return response.data
  }

  async validateHelcimInvoicePayment(
    invoiceId: string,
    payload: {
      rawDataResponse: string
      checkoutToken: string
      secretToken: string
      hashFromClient?: string
    }
  ): Promise<{ success: boolean; data?: { paymentId?: string; invoiceStatus?: string } }> {
    const response = await this.api.post(
      `/customer-portal/invoices/${invoiceId}/checkout/validate`,
      payload
    )
    return response.data
  }

  async getAvailablePaymentMethods(): Promise<{
    availableMethods: string[]
    achEnabled: boolean
    cardEnabled: boolean
    message?: string
  }> {
    const response: AxiosResponse<{
      availableMethods: string[]
      achEnabled: boolean
      cardEnabled: boolean
      message?: string
    }> = await this.api.get('/customer-portal/payment-methods/available')
    return response.data
  }

  async getInvoices(): Promise<{ invoices: any[], total: number }> {
    const response: AxiosResponse<{ invoices: any[], total: number }> = await this.api.get('/customer-portal/invoices')
    return response.data
  }

  async getInvoice(invoiceId: string): Promise<any> {
    console.log('🔍 CustomerAPI: Getting invoice with ID:', invoiceId)
    console.log('🔍 CustomerAPI: Token present:', !!localStorage.getItem('customerToken'))
    
    try {
      const response: AxiosResponse<any> = await this.api.get(`/customer-portal/invoices/${invoiceId}`)
      console.log('✅ CustomerAPI: Invoice data received:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ CustomerAPI: Error getting invoice:', error)
      console.error('❌ CustomerAPI: Error response:', error.response?.data)
      console.error('❌ CustomerAPI: Error status:', error.response?.status)
      throw error
    }
  }

  // Documents
  async getJobDocuments(jobId: string): Promise<{ success: boolean, documents: any[], total: number }> {
    console.log('🔍 CustomerAPI: Getting documents for job ID:', jobId)
    console.log('🔍 CustomerAPI: Token present:', !!localStorage.getItem('customerToken'))
    
    try {
      const response: AxiosResponse<{ success: boolean, documents: any[], total: number }> = await this.api.get(`/customer-portal/jobs/${jobId}/documents`)
      console.log('✅ CustomerAPI: Documents received:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ CustomerAPI: Error getting documents:', error)
      console.error('❌ CustomerAPI: Error response:', error.response?.data)
      console.error('❌ CustomerAPI: Error status:', error.response?.status)
      throw error
    }
  }

  async deleteJobDocument(jobId: string, documentId: string): Promise<{ success: boolean, message: string }> {
    console.log('🔍 CustomerAPI: Deleting document:', documentId, 'from job:', jobId)
    console.log('🔍 CustomerAPI: Token present:', !!localStorage.getItem('customerToken'))
    
    try {
      const response: AxiosResponse<{ success: boolean, message: string }> = await this.api.delete(`/customer-portal/jobs/${jobId}/documents/${documentId}`)
      console.log('✅ CustomerAPI: Document deleted:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ CustomerAPI: Error deleting document:', error)
      console.error('❌ CustomerAPI: Error response:', error.response?.data)
      console.error('❌ CustomerAPI: Error status:', error.response?.status)
      throw error
    }
  }

  async uploadJobDocuments(jobId: string, formData: FormData): Promise<any> {
    console.log('🔍 CustomerAPI: Uploading documents for job ID:', jobId)
    console.log('🔍 CustomerAPI: FormData entries:')
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}: File(name=${value.name}, size=${value.size}, type=${value.type})`)
      } else {
        console.log(`  ${key}:`, value)
      }
    }
    
    // Debug: Check if FormData has any files
    const hasFiles = Array.from(formData.entries()).some(([_, value]) => value instanceof File)
    console.log('🔍 CustomerAPI: FormData has files:', hasFiles)
    console.log('🔍 CustomerAPI: Token present:', !!localStorage.getItem('customerToken'))
    
    try {
      const response: AxiosResponse<any> = await this.api.post(`/customer-portal/jobs/${jobId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      console.log('✅ CustomerAPI: Upload successful:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ CustomerAPI: Upload failed:', error)
      console.error('❌ CustomerAPI: Error response:', error.response?.data)
      console.error('❌ CustomerAPI: Error status:', error.response?.status)
      throw error
    }
  }

  async getInvoicePdf(invoiceId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/customer-portal/invoices/${invoiceId}/pdf`,
      { responseType: 'blob' }
    )
    return response.data
  }

  // Payments - keeping the more detailed version above

  // Documents
  async getDocuments(jobId?: string): Promise<Document[]> {
    const params = jobId ? { jobId } : {}
    const response: AxiosResponse<Document[]> = await this.api.get('/customer-portal/documents', { params })
    return response.data
  }

  async uploadDocument(file: File, jobId?: string, invoiceId?: string): Promise<Document> {
    const formData = new FormData()
    formData.append('file', file)
    if (jobId) formData.append('jobId', jobId)
    if (invoiceId) formData.append('invoiceId', invoiceId)

    const response: AxiosResponse<Document> = await this.api.post(
      '/customer-portal/documents',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  async downloadDocument(documentId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/customer-portal/documents/${documentId}/download`,
      { responseType: 'blob' }
    )
    return response.data
  }

  async deleteDocument(documentId: string): Promise<void> {
    await this.api.delete(`/customer-portal/documents/${documentId}`)
  }

  // Communication
  async sendMessage(jobId: string, message: string): Promise<void> {
    await this.api.post(`/customer-portal/jobs/${jobId}/messages`, { message })
  }

  async getMessages(jobId: string): Promise<any[]> {
    const response = await this.api.get(`/customer-portal/jobs/${jobId}/messages`)
    return response.data
  }

  // Profile Management
  async getProfile(): Promise<any> {
    console.log('🔍 [getProfile] Fetching customer profile')
    try {
      const response = await this.api.get('/customer-portal/profile')
      console.log('✅ [getProfile] Profile fetched successfully')
      return response.data
    } catch (error) {
      console.error('❌ [getProfile] Failed to fetch profile:', error)
      throw error
    }
  }

  async updateProfile(profileData: any): Promise<any> {
    console.log('🔍 [updateProfile] Updating customer profile:', profileData)
    try {
      const response = await this.api.put('/customer-portal/profile', profileData)
      console.log('✅ [updateProfile] Profile updated successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ [updateProfile] Update failed:', error)
      throw error
    }
  }

  async changePassword(passwordData: { currentPassword: string, newPassword: string }): Promise<any> {
    console.log('🔍 [changePassword] Changing customer password')
    try {
      const response = await this.api.put('/customer-portal/profile/password', passwordData)
      console.log('✅ [changePassword] Password changed successfully')
      return response.data
    } catch (error) {
      console.error('❌ [changePassword] Password change failed:', error)
      throw error
    }
  }

  // Tax Document Management
  async uploadTaxDocument(formData: FormData): Promise<any> {
    console.log('🔍 [uploadTaxDocument] Uploading tax exempt document')
    try {
      const response = await this.api.post('/customer-portal/profile/tax-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      console.log('✅ [uploadTaxDocument] Tax document uploaded successfully')
      return response.data
    } catch (error) {
      console.error('❌ [uploadTaxDocument] Upload failed:', error)
      throw error
    }
  }

  async deleteTaxDocument(): Promise<any> {
    console.log('🔍 [deleteTaxDocument] Deleting tax exempt document')
    try {
      const response = await this.api.delete('/customer-portal/profile/tax-document')
      console.log('✅ [deleteTaxDocument] Tax document deleted successfully')
      return response.data
    } catch (error) {
      console.error('❌ [deleteTaxDocument] Delete failed:', error)
      throw error
    }
  }

  // Estimates
  async getEstimates(): Promise<{ estimates: any[], total: number }> {
    console.log('🔍 [getEstimates] Fetching customer estimates')
    try {
      const response: AxiosResponse<{ estimates: any[], total: number }> = await this.api.get('/customer-portal/estimates')
      console.log('✅ [getEstimates] Estimates fetched successfully:', response.data.total)
      return response.data
    } catch (error) {
      console.error('❌ [getEstimates] Failed to fetch estimates:', error)
      throw error
    }
  }

  async getEstimate(estimateId: string, token?: string): Promise<any> {
    console.log('🔍 [getEstimate] Fetching estimate:', estimateId, 'with token:', !!token)
    try {
      const url = token 
        ? `/customer-portal/estimates/${estimateId}?token=${token}`
        : `/customer-portal/estimates/${estimateId}`
      
      // When using token parameter, don't send JWT to avoid conflicts
      const config = token ? { headers: { Authorization: '' } } : {}
      
      const response: AxiosResponse<any> = await this.api.get(url, config)
      console.log('✅ [getEstimate] Estimate fetched successfully')
      return response.data
    } catch (error) {
      console.error('❌ [getEstimate] Failed to fetch estimate:', error)
      throw error
    }
  }

  async approveEstimate(estimateId: string, data: {
    selectedOption?: number
    customerName?: string
    customerEmail?: string
    notes?: string
  }, token?: string): Promise<any> {
    console.log('🔍 [approveEstimate] Approving estimate:', estimateId, 'option:', data.selectedOption)
    try {
      const url = token 
        ? `/customer-portal/estimates/${estimateId}/approve?token=${token}`
        : `/customer-portal/estimates/${estimateId}/approve`
      
      // When using token parameter, don't send JWT to avoid conflicts
      const config = token ? { headers: { Authorization: '' } } : {}
      
      const response: AxiosResponse<any> = await this.api.post(url, data, config)
      console.log('✅ [approveEstimate] Estimate approved successfully')
      return response.data
    } catch (error) {
      console.error('❌ [approveEstimate] Failed to approve estimate:', error)
      throw error
    }
  }

  async declineEstimate(estimateId: string, data: {
    reason?: string
  }, token?: string): Promise<any> {
    try {
      const url = token
        ? `/customer-portal/estimates/${estimateId}/decline?token=${token}`
        : `/customer-portal/estimates/${estimateId}/decline`

      const config = token ? { headers: { Authorization: '' } } : {}

      const response: AxiosResponse<any> = await this.api.post(url, data, config)
      return response.data
    } catch (error) {
      console.error('Failed to decline estimate:', error)
      throw error
    }
  }

  async getAvailableSlots(estimateId: string, params: {
    startDate?: string
    endDate?: string
    duration?: number
  }, token?: string): Promise<any> {
    console.log('🔍 [getAvailableSlots] Getting slots for estimate:', estimateId)
    try {
      const queryParams = new URLSearchParams()
      if (token) queryParams.append('token', token)
      if (params.startDate) queryParams.append('startDate', params.startDate)
      if (params.endDate) queryParams.append('endDate', params.endDate)
      if (params.duration) queryParams.append('duration', params.duration.toString())
      
      const url = `/customer-portal/estimates/${estimateId}/available-slots?${queryParams.toString()}`
      const config = token ? { headers: { Authorization: '' } } : {}
      
      const response: AxiosResponse<any> = await this.api.get(url, config)
      console.log('✅ [getAvailableSlots] Slots retrieved successfully')
      return response.data
    } catch (error) {
      console.error('❌ [getAvailableSlots] Failed to get slots:', error)
      throw error
    }
  }

  async scheduleAppointment(estimateId: string, data: {
    appointmentTime: string
    technicianId: string
    duration?: number
    notes?: string
  }, token?: string): Promise<any> {
    console.log('🔍 [scheduleAppointment] Scheduling appointment for estimate:', estimateId)
    try {
      const url = token 
        ? `/customer-portal/estimates/${estimateId}/schedule-appointment?token=${token}`
        : `/customer-portal/estimates/${estimateId}/schedule-appointment`
      
      const config = token ? { headers: { Authorization: '' } } : {}
      
      const response: AxiosResponse<any> = await this.api.post(url, data, config)
      console.log('✅ [scheduleAppointment] Appointment scheduled successfully')
      return response.data
    } catch (error) {
      console.error('❌ [scheduleAppointment] Failed to schedule appointment:', error)
      throw error
    }
  }

  // Quote Request from Cart
  async submitQuoteRequest(data: {
    items: Array<{
      id?: string
      priceBookItemId?: string
      name: string
      description?: string
      quantity: number
      unitPrice: number
      itemType?: string
      isTaxable?: boolean
    }>
    serviceAddress?: string
    notes?: string
  }): Promise<{
    success: boolean
    message: string
    estimate: {
      id: string
      estimateNumber: string
      title: string
      total: number
      status: string
      createdAt: string
    }
    emailSent: boolean
    nextSteps: string
  }> {
    console.log('🔍 [submitQuoteRequest] Submitting quote request with', data.items.length, 'items')
    try {
      const response: AxiosResponse<any> = await this.api.post('/customer-portal/quote-request', data)
      console.log('✅ [submitQuoteRequest] Quote request submitted successfully:', response.data.estimate?.estimateNumber)
      return response.data
    } catch (error) {
      console.error('❌ [submitQuoteRequest] Failed to submit quote request:', error)
      throw error
    }
  }

  // Special Order Categories
  async getSpecialOrderCategories(): Promise<{
    success: boolean
    categories: Array<{
      id: string
      category: string
      description: string
    }>
    businessName: string
  }> {
    console.log('🔍 [getSpecialOrderCategories] Fetching special order categories')
    try {
      const response: AxiosResponse<any> = await this.api.get('/customer-portal/special-order-categories')
      console.log('✅ [getSpecialOrderCategories] Fetched', response.data.categories?.length, 'categories')
      return response.data
    } catch (error) {
      console.error('❌ [getSpecialOrderCategories] Failed to fetch categories:', error)
      throw error
    }
  }

  // Submit Special Order Request
  async submitSpecialOrder(data: {
    category: string
    itemType?: string
    description: string
    imageUrl?: string
  }): Promise<{
    success: boolean
    message: string
    estimate: {
      id: string
      estimateNumber: string
      title: string
      status: string
      createdAt: string
    }
    nextSteps: string
  }> {
    console.log('🔍 [submitSpecialOrder] Submitting special order for category:', data.category)
    try {
      const response: AxiosResponse<any> = await this.api.post('/customer-portal/special-order', data)
      console.log('✅ [submitSpecialOrder] Special order submitted:', response.data.estimate?.estimateNumber)
      return response.data
    } catch (error) {
      console.error('❌ [submitSpecialOrder] Failed to submit special order:', error)
      throw error
    }
  }

  // Upload special order image
  async uploadSpecialOrderImage(file: File): Promise<{ success: boolean; url: string }> {
    console.log('🔍 [uploadSpecialOrderImage] Uploading image:', file.name)
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response: AxiosResponse<any> = await this.api.post('/customer-portal/special-order/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      console.log('✅ [uploadSpecialOrderImage] Image uploaded successfully')
      return response.data
    } catch (error) {
      console.error('❌ [uploadSpecialOrderImage] Failed to upload image:', error)
      throw error
    }
  }

  // ============================================
  // ACH / Recurring Payment Subscriptions
  // ============================================

  // Get customer's active subscriptions
  async getMySubscriptions(): Promise<{
    success: boolean
    data: Array<{
      id: string
      name: string
      description?: string
      amount: number
      interval: string
      intervalCount: number
      status: string
      nextBillingDate?: string
      lastBillingDate?: string
      lastPaymentAmount?: number
      lastPaymentStatus?: string
      startDate: string
      paymentMethodType: string
    }>
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }> {
    console.log('🔍 [getMySubscriptions] Fetching customer subscriptions')
    try {
      const response: AxiosResponse<any> = await this.api.get('/customer-portal/subscriptions')
      console.log('✅ [getMySubscriptions] Fetched', response.data.data?.length, 'subscriptions')
      return response.data
    } catch (error) {
      console.error('❌ [getMySubscriptions] Failed to fetch subscriptions:', error)
      throw error
    }
  }

  // Get saved payment methods (bank accounts)
  async getMyPaymentMethods(): Promise<{
    success: boolean
    data: Array<{
      id: string
      type: string
      last4?: string
      bankName?: string
      isDefault: boolean
      isVerified: boolean
      createdAt: string
    }>
  }> {
    console.log('🔍 [getMyPaymentMethods] Fetching payment methods')
    try {
      const response: AxiosResponse<any> = await this.api.get('/customer-portal/payment-methods')
      console.log('✅ [getMyPaymentMethods] Fetched', response.data.data?.length, 'payment methods')
      return response.data
    } catch (error) {
      console.error('❌ [getMyPaymentMethods] Failed to fetch payment methods:', error)
      throw error
    }
  }

  // Plaid: create link token for customer to link bank (portal)
  async createPlaidLinkToken(): Promise<{ success: boolean; linkToken: string }> {
    const response: AxiosResponse<{ success: boolean; linkToken: string }> = await this.api.post(
      '/customer-portal/plaid/create-link-token'
    )
    return response.data
  }

  // Plaid: exchange public token and save as customer payment method (plaid_bank)
  async exchangePlaidPublicToken(publicToken: string): Promise<{
    success: boolean
    data: Array<{ id: string; bankName: string | null; last4: string | null }>
  }> {
    const response: AxiosResponse<any> = await this.api.post('/customer-portal/plaid/exchange-public-token', {
      publicToken
    })
    return response.data
  }

  // Pay invoice with saved Plaid bank
  async payInvoiceWithPlaid(
    invoiceId: string,
    plaidPaymentMethodId: string
  ): Promise<{
    success: boolean
    transferId?: string
    plaidTransferId?: string
    status?: string
    message?: string
    error?: string
  }> {
    const response: AxiosResponse<any> = await this.api.post(
      `/customer-portal/invoices/${invoiceId}/pay-with-plaid`,
      { plaidPaymentMethodId }
    )
    return response.data
  }

  // Create setup for adding a new card (returns checkoutUrl for redirect, or setupIntentId/clientSecret for embedded)
  async createCardSetupIntent(returnBaseUrl?: string): Promise<{
    success: boolean
    checkoutUrl: string | null
    setupIntentId: string | null
    clientSecret: string | null
    stripeCustomerId: string | null
    publishableKey: string
  }> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/customer-portal/payment-methods/card-setup-intent', {
        returnBaseUrl: returnBaseUrl || undefined
      })
      return response.data
    } catch (error) {
      console.error('Failed to create card setup intent:', error)
      throw error
    }
  }

  // Delete a saved payment method
  async deletePaymentMethod(paymentMethodId: string): Promise<{
    success: boolean
    message: string
  }> {
    console.log('🔍 [deletePaymentMethod] Deleting payment method:', paymentMethodId)
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/customer-portal/payment-methods/${paymentMethodId}`)
      console.log('✅ [deletePaymentMethod] Payment method deleted')
      return response.data
    } catch (error) {
      console.error('❌ [deletePaymentMethod] Failed to delete payment method:', error)
      throw error
    }
  }

  // Get subscription history/payments
  async getSubscriptionPayments(subscriptionId: string): Promise<{
    success: boolean
    data: Array<{
      id: string
      amount: number
      status: string
      paymentDate: string
      paymentMethod: string
    }>
  }> {
    console.log('🔍 [getSubscriptionPayments] Fetching payments for subscription:', subscriptionId)
    try {
      const response: AxiosResponse<any> = await this.api.get(`/customer-portal/subscriptions/${subscriptionId}/payments`)
      console.log('✅ [getSubscriptionPayments] Fetched', response.data.data?.length, 'payments')
      return response.data
    } catch (error) {
      console.error('❌ [getSubscriptionPayments] Failed to fetch payments:', error)
      throw error
    }
  }
}

export const customerApi = new CustomerApiService()
export type { Customer, Business, Job, Invoice, Document }
